// src/pages/ViewReport/AnalysisReportView.js

import React, { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { computeNorwoodScore } from '../../utils/Onboarding/ResultMapping';
import InteractiveCanvas from '../../components/Onboarding/InteractiveCanvas';
import HotspotExplanationBox from '../../components/HotspotExplanationBox';

// Import the logo and WhatsApp
import matchMedicLogo from '../../images/matchmedic_logo_white.png';
import WhatsAppLogo from '../../images/WhatsAppLogo.png';

// ============================================
// CONSTANTS
// ============================================

const BUCKET_TO_PERCENT = {
  none: 10,
  minimal: 30,
  moderate: 50,
  severe: 70,
  full_bald: 90,
};

const HAIR_GRADIENT_ANIMATION_DURATION = 2000;
const GRAY_BAR_ANIMATION_DURATION = 1000;
const DRAG_THRESHOLD = 30;
const HOTSPOT_READY_DELAY = 400;
const CAROUSEL_SLIDE_COUNT = 3;

const PULSE_CONFIG = {
  period: 1800,              // ms - matches shimmer period
  midpoint: 900,             // ms - when first pulse starts
  sequentialDelay: 100,      // ms - delay between each hotspot (left to right)
  colorDuration: 350,        // ms - how long hotspot stays in colored state
  whiteToColor: 120,         // ms - fade from white to severity color
  colorToWhite: 250,         // ms - fade from severity color back to white
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function mapBucketToPercent(bucket) {
  return BUCKET_TO_PERCENT[bucket] ?? 50;
}

function getSegmentColor(level, targetLevel) {
  const green = { r: 34, g: 197, b: 94 };
  const yellow = { r: 234, g: 179, b: 8 };
  const red = { r: 239, g: 68, b: 68 };
  
  let color;
  if (targetLevel <= 2) {
    color = green;
  } else if (targetLevel <= 4) {
    const t = (targetLevel - 1) / 3;
    color = {
      r: Math.round(green.r + (yellow.r - green.r) * t),
      g: Math.round(green.g + (yellow.g - green.g) * t),
      b: Math.round(green.b + (yellow.b - green.b) * t),
    };
  } else {
    const t = (targetLevel - 4) / 3;
    color = {
      r: Math.round(yellow.r + (red.r - yellow.r) * t),
      g: Math.round(yellow.g + (red.g - yellow.g) * t),
      b: Math.round(yellow.b + (red.b - yellow.b) * t),
    };
  }
  
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

function getHotspotColor(value, hueOffset = 0) {
  const v = Math.max(1, Math.min(7, value));
  
  let h, s, l;
  if (v <= 3) {
    const t = (v - 1) / 2;
    h = 120 - t * 70;
    s = 70 + t * 20;
    l = 45 + t * 5;
  } else {
    const t = (v - 3) / 4;
    h = 50 - t * 50;
    s = 90 - t * 5;
    l = 50 + t * 5;
  }
  h = Math.max(0, Math.min(130, h + hueOffset));
  return `hsl(${h}, ${s}%, ${l}%)`;
}



// ============================================
// MAIN COMPONENT
// ============================================

export default function AnalysisReportView({
  analysis,
  metadata,
  originalImageUrl,
  onSwitchTab,
  hasGeneration = false,
  activeTab = 'analysis',
  onTabChange = () => {},
  // View report tracking props
  viewReportId = null,
  reportRequestId = null,
  onContactButtonClick = () => {},
  onPhoneSubmission = () => {},
  onEmailSubmission = () => {},
}) {
  // Console log for debugging
  console.log('[AnalysisReportView] Rendering with:', { 
    hasAnalysis: !!analysis, 
    hasMetadata: !!metadata, 
    hasImage: !!originalImageUrl 
  });

  // ===============================
  // STATE
  // ===============================
  
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const isDrawerExpandedRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselDragStartX, setCarouselDragStartX] = useState(0);
  const [carouselDragDelta, setCarouselDragDelta] = useState(0);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);

  const drawerRef = useRef(null);
  const [drawerHeight, setDrawerHeight] = useState(180);
  
  const [animatedHairLossLevel, setAnimatedHairLossLevel] = useState(0);
  const [animatedThinningPercent, setAnimatedThinningPercent] = useState(0);
  const [animatedHairlinePercent, setAnimatedHairlinePercent] = useState(0);
  const [hasFirstAnimationRun, setHasFirstAnimationRun] = useState(false);
  const isFirstTimeExpandedRef = useRef(false);
  const hasAnimatedThinningRef = useRef(false);
  const hasAnimatedHairlineRef = useRef(false);
  
  const [focusedHotspotId, setFocusedHotspotId] = useState(null);
  const [focusOverlayOpacity, setFocusOverlayOpacity] = useState(0);
  const [spotlightScreenPos, setSpotlightScreenPos] = useState(null);
  const [isHotspotInteractionReady, setIsHotspotInteractionReady] = useState(false);
  const [pulsingHotspots, setPulsingHotspots] = useState({});
  const focusAnimationRef = useRef(null);
  const hotspotReadyTimerRef = useRef(null);
  const pendingHotspotRef = useRef(null);
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 800, height: 800 });

  // ===============================
  // COMPUTED VALUES (with safety checks)
  // ===============================
  
  const displayFirstName = useMemo(() => {
    try {
      const name = metadata?.userName || '';
      if (!name) return '';
      const firstName = name.trim().split(/\s+/)[0] || '';
      if (!firstName) return '';
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    } catch (e) {
      console.error('[AnalysisReportView] Error computing displayFirstName:', e);
      return '';
    }
  }, [metadata]);

  const randomOffsets = useMemo(() => {
    return metadata?.randomOffsets || {
      hairLoss: 0,
      thinning: 0,
      hairline: 0,
    };
  }, [metadata]);

  const targetHairLossLevel = useMemo(() => {
    try {
      if (!analysis) return 3;
      const baseScore = computeNorwoodScore(analysis.baldness_bucket, analysis.hairline_bucket);
      const levelOffset = Math.round((randomOffsets.hairLoss || 0) / 10);
      return Math.min(7, Math.max(1, baseScore + levelOffset));
    } catch (e) {
      console.error('[AnalysisReportView] Error computing targetHairLossLevel:', e);
      return 3;
    }
  }, [analysis, randomOffsets]);

  const targetThinningPercent = useMemo(() => {
    try {
      if (!analysis) return 50;
      const basePercent = mapBucketToPercent(analysis.baldness_bucket);
      return Math.min(90, basePercent + (randomOffsets.thinning || 0));
    } catch (e) {
      console.error('[AnalysisReportView] Error computing targetThinningPercent:', e);
      return 50;
    }
  }, [analysis, randomOffsets]);

  const targetHairlinePercent = useMemo(() => {
    try {
      if (!analysis) return 50;
      const basePercent = mapBucketToPercent(analysis.hairline_bucket);
      return Math.min(90, basePercent + (randomOffsets.hairline || 0));
    } catch (e) {
      console.error('[AnalysisReportView] Error computing targetHairlinePercent:', e);
      return 50;
    }
  }, [analysis, randomOffsets]);

  const titleMessage = useMemo(() => {
    const name = displayFirstName || 'User';
    if (targetHairLossLevel === 1) {
      return `${name}, you have minimal hair loss`;
    } else if (targetHairLossLevel >= 2 && targetHairLossLevel <= 3) {
      return `${name}, it seems like you're losing some hair`;
    } else if (targetHairLossLevel >= 4 && targetHairLossLevel <= 5) {
      return `${name}, analysis shows you have hair loss`;
    } else {
      return `${name}, you lost significant hair`;
    }
  }, [displayFirstName, targetHairLossLevel]);

  const hairGeometry = useMemo(() => {
    try {
      if (metadata?.hairGeometry) {
        return metadata.hairGeometry;
      }
      const imgW = imageSize.width || 800;
      const imgH = imageSize.height || 800;
      return {
        centerHairPoint: { x: imgW * 0.5, y: imgH * 0.18 },
        rightRecedingPoint: { x: imgW * 0.32, y: imgH * 0.25 },
        leftRecedingPoint: { x: imgW * 0.68, y: imgH * 0.25 },
        rightTemplePoint: { x: imgW * 0.22, y: imgH * 0.35 },
        leftTemplePoint: { x: imgW * 0.78, y: imgH * 0.35 },
      };
    } catch (e) {
      console.error('[AnalysisReportView] Error computing hairGeometry:', e);
      return {
        centerHairPoint: { x: 400, y: 144 },
        rightRecedingPoint: { x: 256, y: 200 },
        leftRecedingPoint: { x: 544, y: 200 },
        rightTemplePoint: { x: 176, y: 280 },
        leftTemplePoint: { x: 624, y: 280 },
      };
    }
  }, [metadata, imageSize]);

  // Hotspots - with safety checks
  const hotspots = useMemo(() => {
    try {
      if (!analysis || !hairGeometry || !imageLoaded) return [];
      
      const HOTSPOT_THRESHOLD = 3;
      const HOTSPOT_SCREEN_SIZE = 36;
      const spots = [];
      
      const topThinning = analysis.top_thinning ?? 1;
      const patternReceding = analysis.pattern_receding ?? 1;
      const patternRecedingObviousSide = analysis.pattern_receding_obvious_side ?? 'image_right';
      const templesReceding = analysis.temples_receding ?? 1;
      const templesRecedingObviousSide = analysis.temples_receding_obvious_side ?? 'image_right';
      
      const mapSide = (side) => side === 'image_right' ? 'right' : 'left';
      
      let hasTopHair = false;
      let hasReceding = false;
      
      if (topThinning > HOTSPOT_THRESHOLD && hairGeometry.centerHairPoint) {
        spots.push({
          id: 'top-thinning',
          centerX: hairGeometry.centerHairPoint.x,
          centerY: hairGeometry.centerHairPoint.y,
          screenSize: HOTSPOT_SCREEN_SIZE,
          value: topThinning,
          label: 'Top Thinning',
          color: getHotspotColor(topThinning),
        });
        hasTopHair = true;
      }
      
      if (patternReceding > HOTSPOT_THRESHOLD) {
        const side = mapSide(patternRecedingObviousSide);
        const point = side === 'right' ? hairGeometry.rightRecedingPoint : hairGeometry.leftRecedingPoint;
        if (point) {
          spots.push({
            id: `pattern-receding-${side}`,
            centerX: point.x,
            centerY: point.y,
            screenSize: HOTSPOT_SCREEN_SIZE,
            value: patternReceding,
            label: 'Pattern Receding',
            color: getHotspotColor(patternReceding),
          });
          hasReceding = true;
        }
      }
      
      if (templesReceding > HOTSPOT_THRESHOLD) {
        const side = mapSide(templesRecedingObviousSide);
        const point = side === 'right' ? hairGeometry.rightTemplePoint : hairGeometry.leftTemplePoint;
        if (point) {
          spots.push({
            id: `temples-receding-${side}`,
            centerX: point.x,
            centerY: point.y,
            screenSize: HOTSPOT_SCREEN_SIZE,
            value: templesReceding,
            label: 'Temple Receding',
            color: getHotspotColor(templesReceding),
          });
        }
      }
      
      // Ensure at least 2 hotspots
      if (spots.length < 2 && !hasReceding) {
        const side = mapSide(patternRecedingObviousSide);
        const point = side === 'right' ? hairGeometry.rightRecedingPoint : hairGeometry.leftRecedingPoint;
        if (point) {
          spots.push({
            id: `pattern-receding-${side}`,
            centerX: point.x,
            centerY: point.y,
            screenSize: HOTSPOT_SCREEN_SIZE,
            value: patternReceding,
            label: 'Pattern Receding',
            color: getHotspotColor(patternReceding),
          });
        }
      }
      
      if (spots.length < 2 && !hasTopHair && hairGeometry.centerHairPoint) {
        spots.push({
          id: 'top-thinning',
          centerX: hairGeometry.centerHairPoint.x,
          centerY: hairGeometry.centerHairPoint.y,
          screenSize: HOTSPOT_SCREEN_SIZE,
          value: topThinning,
          label: 'Top Thinning',
          color: getHotspotColor(topThinning),
        });
      }
      
      console.log('[AnalysisReportView] Generated hotspots:', spots.length);
      return spots;
    } catch (e) {
      console.error('[AnalysisReportView] Error computing hotspots:', e);
      return [];
    }
  }, [analysis, hairGeometry, imageLoaded]);

  // Create hotspot icon - three-layered design with "?" like original
  const createHotspotIcon = useCallback((color, isPulsing) => {
    // Transition duration depends on direction:
    // - When pulsing (white → color): use whiteToColor duration
    // - When not pulsing (color → white): use colorToWhite duration
    const transitionDuration = isPulsing ? PULSE_CONFIG.whiteToColor : PULSE_CONFIG.colorToWhite;
    const transition = `background-color ${transitionDuration}ms ease-out`;
    const textTransition = `color ${transitionDuration}ms ease-out`;
    
    return (
      <div 
        className="w-full h-full rounded-full flex items-center justify-center"
        style={{ 
          backgroundColor: isPulsing ? color : 'white',
          padding: '3px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          transition,
        }}
      >
        <div 
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{ 
            backgroundColor: color,
            padding: '3px',
          }}
        >
          <div 
            className="w-full h-full rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: isPulsing ? color : 'white',
              transition,
            }}
          >
            <span style={{ 
              color: isPulsing ? 'white' : '#6B7280',
              fontSize: '1.5em',
              fontWeight: '2000',
              lineHeight: 1,
              transition: textTransition,
            }}>?</span>
          </div>
        </div>
      </div>
    );
  }, []);

  // Visible hotspots
  const visibleHotspots = useMemo(() => {
    try {
      if (focusedHotspotId && focusOverlayOpacity > 0) {
        return [];
      }
      return hotspots.map(hotspot => ({
        ...hotspot,
        icon: createHotspotIcon(hotspot.color, pulsingHotspots[hotspot.id] || false),
      }));
    } catch (e) {
      console.error('[AnalysisReportView] Error computing visibleHotspots:', e);
      return [];
    }
  }, [hotspots, focusedHotspotId, focusOverlayOpacity, pulsingHotspots, createHotspotIcon]);

  const focusedHotspotSeverity = useMemo(() => {
    if (!focusedHotspotId) return 4;
    const hotspot = hotspots.find(h => h.id === focusedHotspotId);
    return hotspot?.value ?? 4;
  }, [focusedHotspotId, hotspots]);

  const explanationBoxBounds = useMemo(() => ({
    top: 88,
    bottom: drawerHeight + 20,
    left: 16,
    right: 16,
  }), [drawerHeight]);

  const canvasPositioning = useMemo(() => {
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 400;
    
    const availableHeight = viewportHeight - 88 - drawerHeight - 40;
    const maxContainerWidth = Math.min(viewportWidth - 48, 672);
    const naturalCanvasHeight = maxContainerWidth;
    
    let scale = 1.0;
    if (naturalCanvasHeight > availableHeight) {
      scale = availableHeight / naturalCanvasHeight;
    }
    
    const scaledCanvasHeight = naturalCanvasHeight * scale;
    const paddingTop = Math.max(20, (availableHeight - scaledCanvasHeight) / 2);
    
    return { scale, paddingTop: `${paddingTop}px` };
  }, [drawerHeight]);

  // ===============================
  // EFFECTS
  // ===============================
  
  // Measure drawer height
  useLayoutEffect(() => {
    const updateDrawerHeight = () => {
      if (drawerRef.current) {
        setDrawerHeight(drawerRef.current.offsetHeight);
      }
    };
    updateDrawerHeight();
    window.addEventListener('resize', updateDrawerHeight);
    const observer = drawerRef.current ? new ResizeObserver(updateDrawerHeight) : null;
    if (observer && drawerRef.current) observer.observe(drawerRef.current);
    return () => {
      window.removeEventListener('resize', updateDrawerHeight);
      if (observer) observer.disconnect();
    };
  }, [isDrawerExpanded]);

  // Auto-expand drawer
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDrawerExpanded(true);
      isDrawerExpandedRef.current = true;
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // First-time animation
  useEffect(() => {
    if (isDrawerExpanded && !isFirstTimeExpandedRef.current) {
      isFirstTimeExpandedRef.current = true;
      setAnimatedHairLossLevel(0);
      
      const timer = setTimeout(() => {
        let level = 0;
        const interval = setInterval(() => {
          level++;
          setAnimatedHairLossLevel(level);
          if (level >= targetHairLossLevel) {
            clearInterval(interval);
            setHasFirstAnimationRun(true);
          }
        }, HAIR_GRADIENT_ANIMATION_DURATION / targetHairLossLevel);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isDrawerExpanded, targetHairLossLevel]);

  // Thinning animation
  useEffect(() => {
    if (carouselIndex === 1 && !hasAnimatedThinningRef.current && isDrawerExpanded) {
      hasAnimatedThinningRef.current = true;
      setAnimatedThinningPercent(0);
      setTimeout(() => {
        const start = Date.now();
        const interval = setInterval(() => {
          const progress = Math.min((Date.now() - start) / GRAY_BAR_ANIMATION_DURATION, 1);
          setAnimatedThinningPercent(Math.round(targetThinningPercent * progress));
          if (progress >= 1) clearInterval(interval);
        }, 16);
      }, 100);
    }
  }, [carouselIndex, isDrawerExpanded, targetThinningPercent]);

  // Hairline animation
  useEffect(() => {
    if (carouselIndex === 2 && !hasAnimatedHairlineRef.current && isDrawerExpanded) {
      hasAnimatedHairlineRef.current = true;
      setAnimatedHairlinePercent(0);
      setTimeout(() => {
        const start = Date.now();
        const interval = setInterval(() => {
          const progress = Math.min((Date.now() - start) / GRAY_BAR_ANIMATION_DURATION, 1);
          setAnimatedHairlinePercent(Math.round(targetHairlinePercent * progress));
          if (progress >= 1) clearInterval(interval);
        }, 16);
      }, 100);
    }
  }, [carouselIndex, isDrawerExpanded, targetHairlinePercent]);

  // Hotspot pulse animation - sort by X position, sequential left to right
  useEffect(() => {
    if (hotspots.length === 0 || focusedHotspotId) {
      setPulsingHotspots({});
      return;
    }
    
    // Sort hotspots by X position (left to right) and get their IDs
    const sortedHotspots = [...hotspots].sort((a, b) => a.centerX - b.centerX);
    const hotspotIds = sortedHotspots.map(h => h.id);
    
    const { period: PERIOD, colorDuration: PULSE_DURATION, midpoint: MIDPOINT, sequentialDelay: SEQUENTIAL_DELAY } = PULSE_CONFIG;
    
    // Generate pulse times: leftmost first, then sequential
    const pulseTimes = hotspotIds.map((_, index) => MIDPOINT + (index * SEQUENTIAL_DELAY));
    
    let cycleStart = performance.now();
    let animationId;
    
    const tick = () => {
      const now = performance.now();
      const elapsed = now - cycleStart;
      
      // Reset cycle
      if (elapsed >= PERIOD) {
        cycleStart = now - (elapsed % PERIOD);
      }
      
      const cycleTime = elapsed % PERIOD;
      const newState = {};
      
      hotspotIds.forEach((id, index) => {
        const pulseStart = pulseTimes[index];
        const pulseEnd = pulseStart + PULSE_DURATION;
        if (cycleTime >= pulseStart && cycleTime < pulseEnd) {
          newState[id] = true;
        }
      });
      
      setPulsingHotspots(newState);
      animationId = requestAnimationFrame(tick);
    };
    
    animationId = requestAnimationFrame(tick);
    
    return () => {
      cancelAnimationFrame(animationId);
      setPulsingHotspots({});
    };
  }, [hotspots, focusedHotspotId]);

  // ===============================
  // CALLBACKS
  // ===============================

  const animateFocusOverlay = useCallback((target, duration = 300) => {
    if (focusAnimationRef.current) cancelAnimationFrame(focusAnimationRef.current);
    
    const startVal = focusOverlayOpacity;
    const startTime = performance.now();
    
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setFocusOverlayOpacity(startVal + (target - startVal) * eased);
      
      if (progress < 1) {
        focusAnimationRef.current = requestAnimationFrame(animate);
      } else {
        focusAnimationRef.current = null;
        if (target === 0) {
          setFocusedHotspotId(null);
          setSpotlightScreenPos(null);
        }
      }
    };
    
    focusAnimationRef.current = requestAnimationFrame(animate);
  }, [focusOverlayOpacity]);

  const triggerHotspotFocus = useCallback((id, pos) => {
    setFocusedHotspotId(id);
    setSpotlightScreenPos(pos);
    animateFocusOverlay(0.8, 300);
  }, [animateFocusOverlay]);

  const dismissHotspotFocus = useCallback(() => {
    if (focusedHotspotId || focusOverlayOpacity > 0) {
      animateFocusOverlay(0, 300);
    }
  }, [focusedHotspotId, focusOverlayOpacity, animateFocusOverlay]);

  // Hotspot interaction ready
  useEffect(() => {
    if (hotspotReadyTimerRef.current) clearTimeout(hotspotReadyTimerRef.current);
    
    const ready = !isDrawerExpanded && imageLoaded;
    
    if (ready) {
      hotspotReadyTimerRef.current = setTimeout(() => {
        setIsHotspotInteractionReady(true);
        if (pendingHotspotRef.current) {
          const id = pendingHotspotRef.current;
          pendingHotspotRef.current = null;
          setTimeout(() => {
            const btn = document.querySelector(`button[data-hotspot-id="${id}"]`);
            if (btn) {
              const rect = btn.getBoundingClientRect();
              const hotspot = hotspots.find(h => h.id === id);
              triggerHotspotFocus(id, {
                screenX: rect.left + rect.width / 2,
                screenY: rect.top + rect.height / 2,
                screenRadius: Math.max(rect.width, rect.height) / 2,
                color: hotspot?.color || '#fff',
              });
            }
          }, 500);
        }
      }, HOTSPOT_READY_DELAY);
    } else {
      setIsHotspotInteractionReady(false);
    }
    
    return () => { if (hotspotReadyTimerRef.current) clearTimeout(hotspotReadyTimerRef.current); };
  }, [isDrawerExpanded, imageLoaded, hotspots, triggerHotspotFocus]);

  // End focus when drawer expands
  useEffect(() => {
    if (isDrawerExpanded && focusedHotspotId) dismissHotspotFocus();
  }, [isDrawerExpanded, focusedHotspotId, dismissHotspotFocus]);

  // Cleanup
  useEffect(() => {
    return () => { if (focusAnimationRef.current) cancelAnimationFrame(focusAnimationRef.current); };
  }, []);

  const handleImageLoad = useCallback(({ width, height }) => {
    console.log('[AnalysisReportView] Image loaded:', width, height);
    setImageLoaded(true);
    setImageSize({ width: width || 800, height: height || 800 });
  }, []);

  const handleHotspotClick = useCallback((id, pos) => {
    if (!isHotspotInteractionReady) {
      pendingHotspotRef.current = id;
      if (isDrawerExpandedRef.current) {
        setIsDrawerExpanded(false);
        isDrawerExpandedRef.current = false;
      }
      return;
    }
    triggerHotspotFocus(id, pos);
  }, [isHotspotInteractionReady, triggerHotspotFocus]);

  const handleCanvasInteraction = useCallback(() => {
    if (isDrawerExpanded) {
      setIsDrawerExpanded(false);
      isDrawerExpandedRef.current = false;
    }
    if (focusedHotspotId && focusOverlayOpacity > 0) dismissHotspotFocus();
  }, [isDrawerExpanded, focusedHotspotId, focusOverlayOpacity, dismissHotspotFocus]);

  // Pull tab drag
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    setDragStartY(e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY);
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const y = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;
    const delta = dragStartY - y;
    if (Math.abs(delta) > DRAG_THRESHOLD) {
      if (delta > 0 && !isDrawerExpanded) {
        setIsDrawerExpanded(true);
        isDrawerExpandedRef.current = true;
      } else if (delta < 0 && isDrawerExpanded) {
        setIsDrawerExpanded(false);
        isDrawerExpandedRef.current = false;
      }
      setIsDragging(false);
    }
  }, [isDragging, dragStartY, isDrawerExpanded]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // ===============================
  // STYLES
  // ===============================

  const colors = {
    topBanner: '#1f1f1f',
    subBanner: '#3a3a3a',
    subBannerText: '#9ca3af',
    footerBg: '#1f1f1f',
    buttonBg: '#ffffff',
    buttonText: '#1f1f1f',
  };

  // ===============================
  // RENDER
  // ===============================

  return (
    <div 
      className="w-full h-screen flex flex-col relative"
      style={{ background: 'linear-gradient(135deg, #6f6f6f 0%, #3b3b3b 100%)' }}
    >
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/logo_pattern_tile.png)`,
          backgroundRepeat: 'repeat',
          backgroundSize: '340px 437px',
        }}
      />
      
      {/* Focus Overlay */}
      {focusOverlayOpacity > 0 && spotlightScreenPos && (
        <div
          className="fixed inset-0 z-40"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${focusOverlayOpacity * 0.85})`,
            pointerEvents: focusOverlayOpacity > 0.1 ? 'auto' : 'none',
          }}
          onClick={dismissHotspotFocus}
        >
          <div
            className="absolute rounded-full"
            style={{
              left: spotlightScreenPos.screenX - 60,
              top: spotlightScreenPos.screenY - 60,
              width: 120,
              height: 120,
              boxShadow: `0 0 0 9999px rgba(0, 0, 0, ${focusOverlayOpacity * 0.85}), inset 0 0 30px rgba(0, 0, 0, 0.5)`,
              border: `3px solid ${spotlightScreenPos.color || '#fff'}`,
              backgroundColor: 'transparent',
            }}
          />
        </div>
      )}
      
      {/* Explanation Box */}
      {focusedHotspotId && focusOverlayOpacity > 0.5 && spotlightScreenPos && (
        <div className="fixed z-50">
          <HotspotExplanationBox
            hotspotId={focusedHotspotId}
            severity={focusedHotspotSeverity}
            spotlightPosition={{ x: spotlightScreenPos.screenX, y: spotlightScreenPos.screenY }}
            spotlightRadius={spotlightScreenPos.screenRadius * 2.5}
            opacity={focusOverlayOpacity > 0.5 ? 1 : focusOverlayOpacity * 2}
            bounds={explanationBoxBounds}
            onClose={dismissHotspotFocus}
          />
        </div>
      )}
      
      {/* Top Banner */}
      <div className="w-full relative z-20" style={{ backgroundColor: colors.topBanner }}>
        <div className="w-full lg:w-2/5 mx-auto flex items-center justify-center px-4 py-3 sm:py-4">
          <img src={matchMedicLogo} alt="Match Medic" className="h-6" style={{ maxWidth: '140px' }} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden">
        <div className="w-full lg:w-2/5 mx-auto h-full">
          <div className="w-full max-w-2xl mx-auto h-full flex items-start">
            <div 
              className="flex-1 flex flex-col px-5 sm:px-6"
              style={{ paddingTop: canvasPositioning.paddingTop, transition: 'padding-top 0.5s ease' }}
            >
              <div 
                className="relative flex-shrink-0"
                style={{ transform: `scale(${canvasPositioning.scale})`, transformOrigin: 'center top', transition: 'transform 0.5s ease' }}
                onClick={handleCanvasInteraction}
              >
                {originalImageUrl ? (
                  <InteractiveCanvas
                    imageUrl={originalImageUrl}
                    objectFit="cover"
                    objectPosition={{ x: 0.5, y: 0.5 }}
                    aspectRatio={1}
                    cropRect={null}
                    overlays={[]}
                    hotspots={visibleHotspots}
                    hotspotsClickable={!isDrawerExpanded}
                    onImageLoad={handleImageLoad}
                    onImageError={(e) => console.error('[AnalysisReportView] Image error:', e)}
                    onHotspotClick={handleHotspotClick}
                  />
                ) : (
                  <div style={{ width: '100%', aspectRatio: '1/1', backgroundColor: '#2a2a2a', borderRadius: '16px' }} className="flex items-center justify-center">
                    <span className="text-gray-400">Image not available</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full lg:w-2/5 mx-auto">
        <div 
          ref={drawerRef}
          className="relative z-30"
          style={{ backgroundColor: colors.footerBg, borderTopLeftRadius: '16px', borderTopRightRadius: '16px', overflow: 'hidden' }}
        >
          {/* Pull Tab */}
          <div
            style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none', touchAction: 'none' }}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div className="flex items-center justify-center py-2.5">
              <div className="w-10 h-1 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.4)' }} />
            </div>
          </div>

          {/* Expandable Content */}
          <div style={{ maxHeight: isDrawerExpanded ? '500px' : '0px', transition: 'max-height 0.5s ease', overflow: 'hidden' }}>
            <div className="px-5 sm:px-6">
              <h2 className="text-white font-semibold text-lg text-center">{titleMessage}</h2>
            </div>
            
            {/* Carousel */}
            <div className="relative" style={{ minHeight: '180px' }}>
              <div
                className="flex"
                style={{
                  transform: `translateX(calc(-${carouselIndex * 100}% + ${carouselDragDelta}px))`,
                  transition: isCarouselDragging ? 'none' : 'transform 0.3s ease',
                }}
                onTouchStart={(e) => { setIsCarouselDragging(true); setCarouselDragStartX(e.touches[0].clientX); }}
                onTouchMove={(e) => { if (isCarouselDragging) setCarouselDragDelta(e.touches[0].clientX - carouselDragStartX); }}
                onTouchEnd={() => {
                  if (isCarouselDragging) {
                    if (carouselDragDelta > 50 && carouselIndex > 0) setCarouselIndex(carouselIndex - 1);
                    else if (carouselDragDelta < -50 && carouselIndex < 2) setCarouselIndex(carouselIndex + 1);
                    setCarouselDragDelta(0);
                    setIsCarouselDragging(false);
                  }
                }}
                onMouseDown={(e) => { setIsCarouselDragging(true); setCarouselDragStartX(e.clientX); }}
                onMouseMove={(e) => { if (isCarouselDragging) setCarouselDragDelta(e.clientX - carouselDragStartX); }}
                onMouseUp={() => {
                  if (isCarouselDragging) {
                    if (carouselDragDelta > 50 && carouselIndex > 0) setCarouselIndex(carouselIndex - 1);
                    else if (carouselDragDelta < -50 && carouselIndex < 2) setCarouselIndex(carouselIndex + 1);
                    setCarouselDragDelta(0);
                    setIsCarouselDragging(false);
                  }
                }}
                onMouseLeave={() => { if (isCarouselDragging) { setCarouselDragDelta(0); setIsCarouselDragging(false); } }}
              >
                {/* Slide 1: Hair Loss Level */}
                <div className="w-full flex-shrink-0 px-5 sm:px-6 py-4">
                  <div className="rounded-xl p-3" style={{ background: 'linear-gradient(90deg, #353535, #727272)' }}>
                    <h3 className="text-white font-semibold text-lg">Hair loss level</h3>
                    <p className="text-gray-400 text-base mb-1">{targetHairLossLevel} out of 7</p>
                    <div className="flex gap-1.5 mb-3">
                      {[1,2,3,4,5,6,7].map((l) => (
                        <div
                          key={l}
                          className="flex-1 h-3 rounded-sm"
                          style={{
                            backgroundColor: l <= (hasFirstAnimationRun ? targetHairLossLevel : animatedHairLossLevel)
                              ? getSegmentColor(l, targetHairLossLevel) : '#6b7280',
                            transition: 'background-color 0.2s',
                          }}
                        />
                      ))}
                    </div>
                    <p className="text-gray-300 text-xs">
                      You would likely be classified as a Norwood {targetHairLossLevel} level hair loss patient.
                    </p>
                  </div>
                </div>

                {/* Slide 2: Thinning */}
                <div className="w-full flex-shrink-0 px-5 sm:px-6 py-4">
                  <div className="rounded-xl px-3 py-5" style={{ background: 'linear-gradient(90deg, #763512, #D38155)' }}>
                    <h3 className="text-white font-semibold text-lg">Thinning</h3>
                    <div className="relative h-5 rounded-full overflow-hidden mb-3 mt-1" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}>
                      <div 
                        className="absolute inset-y-0 left-0 rounded-full flex items-center"
                        style={{ width: `${animatedThinningPercent}%`, backgroundColor: 'white', transition: 'width 0.05s' }}
                      >
                        <span className="text-gray-800 font-semibold text-sm pl-3">{animatedThinningPercent}%</span>
                      </div>
                    </div>
                    <p className="text-white/90 text-xs">
                      {targetThinningPercent <= 30 ? "Your frontal scalp maintains excellent overall density." :
                       targetThinningPercent <= 50 ? "Your frontal scalp maintains good density with subtle variation." :
                       targetThinningPercent <= 70 ? "Your frontal scalp shows moderate thinning." :
                       "Your frontal scalp shows significant thinning."}
                    </p>
                  </div>
                </div>

                {/* Slide 3: Hairline */}
                <div className="w-full flex-shrink-0 px-5 sm:px-6 py-4">
                  <div className="rounded-xl px-3 py-5" style={{ background: 'linear-gradient(90deg, #1B6130, #48AD66)' }}>
                    <h3 className="text-white font-semibold text-lg">Hairline</h3>
                    <div className="relative h-5 rounded-full overflow-hidden mb-3 mt-1" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}>
                      <div 
                        className="absolute inset-y-0 left-0 rounded-full flex items-center"
                        style={{ width: `${animatedHairlinePercent}%`, backgroundColor: 'white', transition: 'width 0.05s' }}
                      >
                        <span className="text-gray-800 font-semibold text-sm pl-3">{animatedHairlinePercent}%</span>
                      </div>
                    </div>
                    <p className="text-white/90 text-xs">
                      {targetHairlinePercent <= 30 ? "Your hairline is well-defined with minimal recession." :
                       targetHairlinePercent <= 50 ? "Your hairline shows some temporal recession." :
                       targetHairlinePercent <= 70 ? "Your hairline shows moderate recession." :
                       "Your hairline shows significant recession."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 pb-2">
                {[0,1,2].map((i) => (
                  <button
                    key={i}
                    onClick={() => setCarouselIndex(i)}
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: i === carouselIndex ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)' }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="h-px mx-5 sm:mx-6" style={{ backgroundColor: '#404040' }} />

          {/* Buttons */}
          <div className="px-5 sm:px-6 py-4">
            <div className="flex gap-3">
              <a
                href="https://wa.me/908503469160?text=Hi!%20I'm%20interested%20in%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-gray-700 px-4 py-2.5 hover:bg-gray-600"
                onClick={() => onContactButtonClick('whatsapp', 'analysis_drawer')}
              >
                <img src={WhatsAppLogo} alt="WhatsApp" className="h-5 w-5" />
                <span className="text-sm font-medium text-white">New Chat</span>
              </a>
              {hasGeneration ? (
                <button
                  onClick={() => onTabChange('generation')}
                  className="flex-1 py-2.5 px-6 text-md font-bold rounded-full text-center"
                  style={{ backgroundColor: colors.buttonBg, color: colors.buttonText }}
                >
                  See simulated results
                </button>
              ) : (
                <a
                  href="/onboarding"
                  className="flex-1 py-2.5 px-6 text-md font-bold rounded-full text-center"
                  style={{ backgroundColor: colors.buttonBg, color: colors.buttonText }}
                >
                  Start onboarding for free
                </a>
              )}
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">Match Medic Quickscan™ all rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
